/* ============================================================================
   pfm-sel.js —— 「选中框 / 控制点」样式的**唯一真源**（2026-10-04 建）
   ============================================================================
   背景（用户原话：「样式还是不一样啊，不是做成公共函数，调用吗？」）：
     画板选区的变换框（.pfm-sel-hd-frame，纯 DOM/CSS）与设计台的对象控制框
     （fabric 控制点，画在 canvas 上）是**两条渲染管线**，此前各自写死了一套样式 ——
     颜色、线型（实线/虚线）、形状（圆角方块/圆）、边中点、旋转点、旋转杆、热区
     全都不一样，只有"屏幕恒定"的算法在 10-04 被统一过。

   本文件 = 两侧共用的那一份：
     · 常量真源 = CSS 变量 --pf-sel-*（写在 assets/pf-tokens.css，全站已加载）
     · DOM 侧（画板）：hd-canvas.css 直接 var(--pf-sel-*)，零 JS
     · Canvas 侧（设计台）：PFMSEL.applyFabric(fabric,{screenScale}) 接管 fabric 控制点，
        PFMSEL.renderControl 逐点按同一批常量绘制 ⇒ 两侧同款

   ⚠️ 改样式请改 pf-tokens.css，**不要**在本文件写死颜色/尺寸（此处只有兜底默认值）。
   ⚠️ 屏幕恒定：fabric 的 cornerSize / offsetY 等是**画布逻辑单位**，必须除以屏幕缩放
      （= 画布 CSS 缩放 × 整体视图缩放）。缩放函数由调用方注入（section 传 pfmScreenScale），
      本文件**不自己猜缩放** —— 那正是 10-04 "漏掉整体缩放"那个 bug 的根源。
   ============================================================================ */
window.PFMSEL = (function () {
  'use strict';

  var SCALE = null;   /* 屏幕缩放函数（唯一真源），由 applyFabric 注入 */
  var CACHE = null;   /* 变量快照（一次性读；主题/调试可 PFMSEL.reset() 清） */
  var CORNER = ['tl', 'tr', 'bl', 'br'];   /* 角手柄的控制点名（fabric 默认 controls） */

  function strVar(name, fb) {
    try {
      var v = (getComputedStyle(document.documentElement).getPropertyValue(name) || '').trim();
      return v || fb;
    } catch (e) { return fb; }
  }
  function numVar(name, fb) {
    var n = parseFloat(strVar(name, ''));
    return isFinite(n) ? n : fb;
  }
  function dashVar() {
    var raw = strVar('--pf-sel-dash', '');
    if (!raw) return null;                                   /* 未填 = 实线（当前统一形态） */
    var a = raw.split(/[\s,]+/).map(parseFloat).filter(function (x) { return isFinite(x); });
    return a.length ? a : null;
  }

  /* 全部样式常量。这里的默认值与 pf-tokens.css 保持一致（变量读不到时才用）。 */
  function tokens() {
    if (CACHE) return CACHE;
    CACHE = {
      color:    strVar('--pf-sel-color', '#534AB7'),
      edgeFill: strVar('--pf-sel-edge-fill', '#EEEDFE'),
      rotFill:  strVar('--pf-sel-rot-fill', '#fff'),
      bw:       numVar('--pf-sel-bw', 1),        /* 框线/描边（屏幕 px） */
      rotBw:    numVar('--pf-sel-rot-bw', 2),    /* 旋转点描边（屏幕 px） */
      corner:   numVar('--pf-sel-corner', 14),   /* 角手柄边长 */
      edge:     numVar('--pf-sel-edge', 11),     /* 边中点手柄边长 */
      rot:      numVar('--pf-sel-rot', 18),      /* 旋转点直径 */
      stem:     numVar('--pf-sel-stem', 23),     /* 框顶 → 旋转点中心（= 旋转杆长度） */
      touch:    numVar('--pf-sel-touch', 28),    /* 触控热区 */
      radius:   numVar('--pf-r-sm', 6),          /* 圆角（复用全局 token） */
      dash:     dashVar()
    };
    return CACHE;
  }

  /* 屏幕 px → 画布逻辑单位 */
  function L(v) {
    var s = SCALE ? SCALE() : 1;
    if (!(s > 0)) s = 1;
    return v / s;
  }

  /* 圆角矩形路径（对应 CSS 的 border-radius，中心在原点） */
  function rr(ctx, w, h, r) {
    r = Math.max(0, Math.min(r, w / 2, h / 2));
    var x = -w / 2, y = -h / 2, rw = x + w, rh = y + h;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(rw - r, y); ctx.arcTo(rw, y, rw, y + r, r);
    ctx.lineTo(rw, rh - r); ctx.arcTo(rw, rh, rw - r, rh, r);
    ctx.lineTo(x + r, rh); ctx.arcTo(x, rh, x, rh - r, r);
    ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  /* fabric 控制点渲染器 —— 与画板 DOM 手柄逐项对齐：
       角手柄：实心圆角方块（14 / r=6）         ←→ .pfm-sel-hd-frame i
       边中点：浅底 + 1px 主色描边（11）        ←→ .pfm-sel-hd-frame i.ed
       旋转点：白底圆 + 2px 主色描边（18）      ←→ .pfm-sel-hd-frame b
     🔴 fabric 调用形式是 `control.render(ctx,left,top,styleOverride,obj)`（method call）
        ⇒ `this` 就是控件本身，故用 this.__pfmKind 区分三类（applyFabric 里打的标签）。 */
  function renderControl(ctx, left, top, styleOverride, obj) {
    var T = tokens();
    var kind = (this && this.__pfmKind) || 'corner';
    var s, fill, stroke, lw;
    if (kind === 'rot') {
      s = L(T.rot);   fill = T.rotFill;  stroke = T.color; lw = L(T.rotBw);
    } else if (kind === 'edge') {
      s = L(T.edge);  fill = T.edgeFill; stroke = T.color; lw = L(T.bw);
    } else {
      s = L(T.corner); fill = T.color;   stroke = null;    lw = 0;
    }
    if (!(s > 0)) return;
    var r = (kind === 'rot') ? s / 2 : T.radius;   /* 旋转点恒为正圆；其余用 --pf-r-sm */
    ctx.save();
    ctx.translate(left, top);
    ctx.lineWidth = lw;
    if (lw > 0) {
      /* 描边画在盒子内沿 —— 与 CSS 的 box-sizing:border-box 同义 */
      rr(ctx, s - lw, s - lw, r - lw / 2);
      ctx.fillStyle = fill; ctx.strokeStyle = stroke;
      ctx.fill(); ctx.stroke();
    } else {
      rr(ctx, s, s, r);
      ctx.fillStyle = fill; ctx.fill();
    }
    ctx.restore();
  }

  /* 接管 fabric 的所有控制点样式 + 屏幕恒定尺寸。返回本次生效的常量（供断言）。 */
  function applyFabric(fabric, opts) {
    opts = opts || {};
    SCALE = (typeof opts.screenScale === 'function') ? opts.screenScale : null;
    CACHE = null;
    if (!fabric || !fabric.Object) return null;
    var T = tokens();
    var proto = fabric.Object.prototype;

    /* 选中框：同一主色 + 实线（虚线节距由 --pf-sel-dash 控制，当前留空 = 实线） */
    proto.borderColor = T.color;
    proto.borderDashArray = T.dash;
    proto.cornerColor = T.color;
    proto.cornerStrokeColor = T.color;
    proto.cornerDashArray = null;
    proto.transparentCorners = false;
    proto.cornerStyle = 'circle';
    proto.padding = 6;

    var CTR = proto.controls || {};
    var keys = Object.keys(CTR);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i], c = CTR[k];
      /* 三类标签：mtr = 旋转点；tl/tr/bl/br = 角；mt/mb/ml/mr = 边中点。
         🔴 不能按"名字长度"判：`ml` 也是 2 个字符，曾被误判成角点（探针 B2 抓到）。 */
      c.__pfmKind = (k === 'mtr') ? 'rot' : (CORNER.indexOf(k) >= 0 ? 'corner' : 'edge');
      c.render = renderControl;      /* 挂实例：不依赖 fabric 的运行时查表 */
    }
    /* 旋转杆：fabric 用 withConnection 画「对象顶边 → 旋转点中心」的连线，
       颜色/线宽跟随 borderColor/borderScaleFactor ⇒ 与画板的 ::before 细杆同款（23px / 1px）。 */
    if (CTR.mtr) CTR.mtr.withConnection = true;

    /* 兜底：默认 render 是运行时查表（"circle"===cornerStyle ? renderCircleControl : renderSquareControl）
       —— 改表对"没被逐个挂 render"的控件也生效（例如别处 clone 出来的对象）。 */
    if (fabric.controlsUtils) {
      fabric.controlsUtils.renderCircleControl = renderControl;
      fabric.controlsUtils.renderSquareControl = renderControl;
    }
    return T;
  }

  return {
    tokens: tokens,
    applyFabric: applyFabric,
    renderControl: renderControl,
    logical: L,
    reset: function () { CACHE = null; }
  };
})();
